import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../css/SearchResults.css';
import FieldResult from './FieldResult';
import { SearchBar } from './SearchBar';
import { getFields } from '../utilities/getFields';
import { coordinateForField, distanceInMeters, formatDistanceMiles, geocodePlace } from '../utilities/geo';
import { getAnalytics, logEvent } from "firebase/analytics";
import { SearchResultsSkeleton } from './Skeletons';
import { SiteFooter } from './SiteChrome';
import { reportCaughtError } from '../utilities/errorTracking';

// A query can be a field name ("Terryville High School") or a place
// ("44139", "Aurora OH"). Name matching runs first; when it comes up dry —
// or the query looks like a zip code — the query is geocoded and results
// are ranked by distance to that place, mirroring the app's search.
const looksLikeZip = (query) => /^\s*\d{5}(-\d{4})?\s*$/.test(query ?? '');

function SearchResults() {
  const [searchParams] = useSearchParams();
  const sQuery = searchParams.get("q");
  const [searchTerm, setSearchTerm] = useState(sQuery);

  const [fieldsSnapshot, setFieldsSnapshot] = useState();
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldsError, setFieldsError] = useState();
  const [placeLabel, setPlaceLabel] = useState();
  const [nearCoordinate, setNearCoordinate] = useState();

  useEffect(() => {
    let isStale = false;

    const search = async () => {
      setFieldsLoading(true);
      setPlaceLabel(undefined);
      setNearCoordinate(undefined);

      const applyResults = (snapshot) => {
        if (!isStale) {
          setFieldsSnapshot(snapshot);
        }
      };
      const applyError = (error) => {
        if (!isStale) {
          setFieldsError(error);
        }
      };

      // First pass: plain name/address matching.
      let nameMatchCount;
      await getFields(
        searchTerm,
        snapshot => {
          nameMatchCount = snapshot.docs.length;
          applyResults(snapshot);
        },
        applyError,
        () => undefined,
      );

      // Second pass: treat the query as a place when name matching came up
      // dry (or it's clearly a zip code) and rank by distance.
      if ((nameMatchCount === 0 || looksLikeZip(searchTerm)) && searchTerm?.trim()) {
        try {
          const place = await geocodePlace(searchTerm);
          if (place && !isStale) {
            setPlaceLabel(place.label);
            setNearCoordinate(place);
            await getFields(
              searchTerm,
              applyResults,
              applyError,
              () => undefined,
              { nearCoordinate: place },
            );
          }
        } catch (error) {
          reportCaughtError(error, 'search-geocode');
        }
      }

      if (!isStale) {
        setFieldsLoading(false);
      }
    };

    search();

    const analytics = getAnalytics();
    logEvent(analytics, 'view_search_results', {
      search_term: searchTerm,
    });

    return () => {
      isStale = true;
    };
  }, [searchTerm]);

  if (fieldsError !== undefined) {
    return <div className="container mt-5">
      <p>There was a problem fetching your results. If refreshing does not solve the problem, please contact <a href="mailto:support@fieldfactsbaseball.com">support</a>.</p>
      <p><a href="/">Go Back</a></p>
    </div>
  }

  return (
   <div className="ff-page">
   <div className="container-lg search-results-content">
    <SearchBar setSearchTerm={setSearchTerm} searchTerm={searchTerm} />

    <div className="search-count">
      {fieldsLoading && <p className="ff-kicker">Searching…</p>}
      {!fieldsLoading && fieldsSnapshot?.docs !== undefined && !nearCoordinate &&
        <p>Your search for <b>{searchTerm}</b> returned <b>{fieldsSnapshot.docs.length}</b> {fieldsSnapshot.docs.length === 1 ? 'result' : 'results'}.</p>}
      {!fieldsLoading && nearCoordinate &&
        <p>Fields closest to <b>{placeLabel ?? searchTerm}</b>:</p>}
      {!fieldsLoading && fieldsSnapshot?.docs?.length === 0 && <div className="troubleshooting">
        <p>If you are having trouble finding a field, try searching for the name of the city or the zip code.</p>
        <p>Here are some examples of valid searches:</p>
        <ul>
          <li>Terryville High School</li>
          <li>Little River</li>
          <li>44141</li>
          <li>OH</li>
        </ul>
        </div>}
    </div>

    {fieldsLoading && <SearchResultsSkeleton />}

    <div className="fields">

      {(fieldsSnapshot?.docs !== undefined && fieldsLoading === false) && fieldsSnapshot.docs.map(f => {
        const coordinate = nearCoordinate ? coordinateForField(f.data) : undefined;
        const distanceLabel = coordinate
          ? formatDistanceMiles(distanceInMeters(nearCoordinate, coordinate))
          : undefined;
        return <FieldResult key={f.id} fieldSnapshot={f} distanceLabel={distanceLabel} />;
      })}

    </div>

   </div>
   <SiteFooter />
   </div>
  );
}

export default SearchResults;
